import React, { useEffect, useState } from "react";
import { Select, Spin } from "antd";
import Swal from "sweetalert2";
import {
  getApiBaseUrl,
  ADD_ACTIVITY,
  LOGIN,
  GET_LOCATIONS,
} from "../../Config/Config";

const { Option } = Select;

const AddDryerEquip = () => {
  const [blockOptions, setBlockOptions] = useState([]);
  const [formData, setFormData] = useState({
    GroupName: "",
  });

  const [loading, setLoading] = useState(false);
  const [selectedBlock, setSelectedBlock] = useState(null); // Block ID
  const [selectedBlockName, setSelectedBlockName] = useState(""); // Block Name for display/log

  const user = JSON.parse(sessionStorage.getItem("user"));

  const [authLoading, setAuthLoading] = useState(false);
  const [askPassword, setAskPassword] = useState(false);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");

  const [locations, setLocations] = useState([]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const fetchBlocks = async () => {
    try {
      const response = await fetch(
        `${getApiBaseUrl()}/api/getlocationsforPresures`
      );
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const result = await response.json();
      const blocks = result?.ResultData || [];
      setBlockOptions(blocks);
    } catch (err) {
      console.error("Failed to fetch blocks:", err);
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await fetch(`${getApiBaseUrl()}${GET_LOCATIONS}`);
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const result = await response.json();
      const locationList = result?.ResultData || [];
      setLocations(locationList);
    } catch (error) {
      console.error("Failed to fetch locations:", error);
    }
  };

  useEffect(() => {
    fetchBlocks();
    fetchLocations();
  }, []);

  const handleBlockChange = (value) => {
    setSelectedBlock(value);

    // Find and set the block name for audit log
    const selectedOption = blockOptions.find((b) => b.BlockId === value);
    const selectedLoc = locations.find((l) => l.BlockId === value);

    const blockName = selectedOption?.BlockName || selectedLoc?.BlockName || "Unknown Block";
    setSelectedBlockName(blockName);

    setFormData((prev) => ({
      ...prev,
      BlockId: value,
    }));
  };

  const handelFormSubmit = (e) => {
    e.preventDefault();

    if (!selectedBlock || !formData.GroupName.trim()) {
      setError("Please fill in all required fields: Group Name and Block.");
      Swal.fire({
        icon: "warning",
        title: "Validation Error",
        text: "Please fill in all required fields: Group Name and Block.",
      });
      return;
    }

    if (!user?.UserID) {
      setError("Invalid user data. Please log in again.");
      Swal.fire({
        icon: "error",
        title: "User Data Error",
        text: "Invalid user data. Please log in again.",
      });
      return;
    }

    setAskPassword(true);
  };

  const handleAuth = async () => {
    if (!password) {
      setError("Please enter your password.");
      return;
    }

    setAuthLoading(true);
    setError("");

    try {
      if (!user?.UserName) {
        throw new Error("User name not found in session data.");
      }

      const payload = {
        username: user.UserName,
        password,
      };

      const res = await fetch(`${getApiBaseUrl()}${LOGIN}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (data.status) {
        setAskPassword(false);
        setPassword("");
        await performAdd();
      } else {
        throw new Error(data.Message || "Invalid credentials. Please try again.");
      }
    } catch (err) {
      setError(err.message || "Unable to authenticate. Please try again.");
    } finally {
      setAuthLoading(false);
    }
  };

  const performAdd = async () => {
    const logActivity = async (isSuccess, notes) => {
      try {
        await fetch(`${getApiBaseUrl()}${ADD_ACTIVITY}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ActivityType: 1,
            PerformedBy: user?.UserID,
            PerformedOn: new Date().toISOString().slice(0, 19).replace("T", " "),
            Notes: notes,
            Location: "PressureGroup",
            IsSucces: isSuccess ? 1 : 0,
          }),
        });
      } catch (err) {
        console.error("Failed to log activity:", err);
      }
    };

    const payload = {
      CreateBy: user?.UserID,
      GroupName: formData.GroupName.trim(),
      BlockId: selectedBlock,
    };

    try {
      setLoading(true);

      const response = await fetch(`${getApiBaseUrl()}/Blocks/addPressureGroup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const result = await response.json();
      const status = result?.ResultData?.[0];

      if (response.ok && status?.Status === "Success") {
        await logActivity(
          true,
          `Group "${formData.GroupName}" created successfully for block "${selectedBlockName}"`
        );

        setFormData({ GroupName: "" });
        setSelectedBlock(null);
        setSelectedBlockName("");

        Swal.fire("Success", "Group added successfully!", "success").then(() => {
          window.location.reload();
        });
      } else {
        await logActivity(
          false,
          `Group "${formData.GroupName}" creation failed for block "${selectedBlockName}"`
        );
        Swal.fire("Failed", status?.ResultMessage || "Something went wrong!", "error");
      }
    } catch (error) {
      console.error("Network error:", error);
      await logActivity(
        false,
        `Error creating Group "${formData.GroupName}" for block "${selectedBlockName}": ${error.message}`
      );
      Swal.fire("Error", "Unable to connect to server.", "error");
    } finally {
      setLoading(false);
      setAskPassword(false);
      setPassword("");
      setError("");
    }
  };

  const clearData = () => {
    setFormData({ GroupName: "" });
    setSelectedBlock(null);
    setSelectedBlockName("");
  };

  useEffect(() => {
    const offcanvas = document.getElementById("AddDryerEquipOffcanvas");
    if (offcanvas) {
      const handleClose = () => {
        clearData();
      };
      offcanvas.addEventListener("hidden.bs.offcanvas", handleClose);
      return () => {
        offcanvas.removeEventListener("hidden.bs.offcanvas", handleClose);
      };
    }
  }, []);

  return (
    <>
      <style>
        {`
        @media (max-width: 576px) {
          #AddDryerEquipOffcanvas { width: 90% !important; }
        }
        @media (min-width: 577px) and (max-width: 992px) {
          #AddDryerEquipOffcanvas { width: 55% !important; }
        }
        @media (min-width: 993px) and (max-width: 1440px) {
          #AddDryerEquipOffcanvas { width: 24% !important; }
        }
        @media (min-width: 1441px) {
          #AddDryerEquipOffcanvas { width: 15% !important; }
        }
        `}
      </style>

      {(loading || authLoading) && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{
            zIndex: 1050,
            backdropFilter: "blur(4px)",
            backgroundColor: "rgba(255, 255, 255, 0.2)",
          }}
        >
          <Spin size="large" tip="Processing..." />
        </div>
      )}

      <div className="offcanvas offcanvas-end" tabIndex="-1" id="AddDryerEquipOffcanvas">
        <form onSubmit={handelFormSubmit}>
          <div className="offcanvas-header">
            <h5 className="offcanvas-title me-4">Add Group</h5>
            <div className="d-flex align-items-center ms-auto">
              <button
                type="submit"
                className="btn btn-sm btn-outline-success me-2"
                title="Save"
                disabled={askPassword}
              >
                <i className="fa fa-check fs-4"></i>
              </button>
              <button
                type="button"
                className="btn-close text-reset"
                data-bs-dismiss="offcanvas"
                aria-label="Close"
                disabled={askPassword}
              ></button>
            </div>
          </div>

          <div className="offcanvas-body">
            {error && (
              <div className="alert alert-danger" role="alert">
                {error}
              </div>
            )}

            <div className="mb-3">
              <label className="form-label">
                Group Name <span className="text-danger ms-1">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                name="GroupName"
                placeholder="Enter Group Name"
                value={formData.GroupName}
                onChange={handleChange}
                required
              />
            </div>

            <div className="mb-3 w-100">
              <label className="form-label">
                Block Name <span className="text-danger ms-1">*</span>
              </label>
              <Select
                showSearch
                placeholder="Select a Block"
                optionFilterProp="children"
                onChange={handleBlockChange}
                value={selectedBlock}
                filterOption={(input, option) =>
                  option.children.toLowerCase().includes(input.toLowerCase())
                }
                style={{ width: "100%" }}
                aria-required
              >
                {blockOptions.map((block) => (
                  <Option key={block.BlockId} value={block.BlockId}>
                    {block.BlockName}
                  </Option>
                ))}
                {locations.map((loc) => (
                  <Option key={loc.BlockId} value={loc.BlockId}>
                    {loc.BlockName}
                  </Option>
                ))}
              </Select>
            </div>

            {askPassword && (
              <div className="mt-2">
                <div className="mb-3 w-lg-75 m-auto">
                  <label className="form-label">
                    Confirm Password <span className="text-danger"> *</span>
                  </label>
                  <div className="pass-group">
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      className="pass-input form-control"
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        setError("");
                      }}
                      onCopy={(e) => e.preventDefault()}
                      onPaste={(e) => e.preventDefault()}
                      onCut={(e) => e.preventDefault()}
                      placeholder="Enter Password"
                    />
                    <span
                      className={`ti toggle-password ${
                        showPassword ? "ti-eye" : "ti-eye-off"
                      }`}
                      onClick={() => setShowPassword(!showPassword)}
                    ></span>
                  </div>
                </div>

                {error && (
                  <div className="alert alert-danger" role="alert">
                    {error}
                  </div>
                )}

                <button
                  className="btn btn-primary btn-sm me-2"
                  onClick={handleAuth}
                  disabled={authLoading || !password}
                >
                  {authLoading ? "Verifying..." : "Confirm"}
                </button>
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => {
                    setAskPassword(false);
                    setPassword("");
                    setError("");
                  }}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        </form>
      </div>
    </>
  );
};

export default AddDryerEquip;